import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaChevronRight, FaArrowLeft } from 'react-icons/fa';
import SEO from '../../Components/utils/SEO';

const API = process.env.REACT_APP_Backend_Host_Name;

// Gradient palette for B2B categories (cycles through these)
const GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #fd746c 0%, #ff9068 100%)',
    'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
    'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
    'linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)',
];

const getGradient = (index) => GRADIENTS[index % GRADIENTS.length];

const B2BCategories = () => {
    const [categoriesData, setCategoriesData] = useState([]);
    const [selectedMain, setSelectedMain] = useState(null);
    const [selectedSub, setSelectedSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const loadCategories = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/v1/categories/with-counts?b2b=true`);
                setCategoriesData(res.data.categories || []);
            } catch (err) {
                console.error('Error fetching B2B categories:', err);
                // Fallback: try basic categories endpoint
                try {
                    const res2 = await axios.get(`${API}/api/v1/categories?b2b=true`);
                    setCategoriesData(res2.data.categories || []);
                } catch (e) {
                    console.error(e);
                }
            } finally {
                setLoading(false);
            }
        };
        loadCategories();
    }, []);

    // Navigate to B2B products page with filters
    const navigateToProducts = (main, sub = '', child = '') => {
        const params = new URLSearchParams();
        params.set('category', main);
        if (sub) params.set('subcategory', sub);
        if (child) params.set('childcategory', child);
        navigate(`/b2b/products-services?${params.toString()}`);
    };

    const handleMainClick = (cat) => {
        setSelectedMain(cat);
        setSelectedSub(null);
        window.scrollTo(0, 0);
    };

    const handleSubClick = (sub, mainName) => {
        if (sub.children && sub.children.length > 0) {
            setSelectedSub({ ...sub, mainName });
            window.scrollTo(0, 0);
        } else {
            navigateToProducts(mainName, sub.name);
        }
    };

    const handleChildClick = (childName, subName, mainName) => {
        navigateToProducts(mainName, subName, childName);
    };

    const goBack = () => {
        if (selectedSub) {
            setSelectedSub(null);
        } else if (selectedMain) {
            setSelectedMain(null);
        }
        window.scrollTo(0, 0);
    };

    const getGradientByName = (name) => {
        const idx = categoriesData.findIndex(c => c.name === name);
        return getGradient(idx >= 0 ? idx : 0);
    };

    const seoTitle = selectedSub
        ? `${selectedSub.name} B2B categories`
        : selectedMain
            ? `${selectedMain.name} B2B categories`
            : 'B2B categories';

    const seoDescription = selectedSub
        ? `Explore ${selectedSub.name} wholesale listings by subcategory and type.`
        : selectedMain
            ? `Browse ${selectedMain.name} B2B subcategories and wholesale listings.`
            : 'Browse wholesale categories tailored for your business sourcing needs.';

    // ── Loading skeleton ────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="categoriesPage b2bCategoriesPage">
                <SEO title={seoTitle} description={seoDescription} canonicalPath="/b2b/categories" />
                <section className="mainSection">
                    <div className="catPageHeader b2bCatPageHeader">
                        <span className="b2bCatPageBadge">B2B Wholesale</span>
                        <h1>B2B Categories</h1>
                        <p>Browse wholesale categories tailored to your business needs</p>
                    </div>
                    <div className="catGrid">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="catCard skeleton">
                                <div className="catCardBanner"></div>
                                <div className="catCardBody"><div className="skeletonLine"></div></div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    // ── Child categories view ───────────────────────────────────────────────
    if (selectedSub) {
        return (
            <div className="categoriesPage b2bCategoriesPage">
                <SEO title={seoTitle} description={seoDescription} canonicalPath="/b2b/categories" />
                <section className="mainSection">
                    <button className="catBackBtn" onClick={goBack}>
                        <FaArrowLeft /> Back to {selectedMain.name}
                    </button>

                    <div className="catSubBanner" style={{ background: getGradientByName(selectedMain.name) }}>
                        <div className="catSubBannerContent">
                            <span className="catSubBannerIcon">{selectedSub.icon || selectedMain.icon}</span>
                            <div>
                                <h2>{selectedSub.name}</h2>
                                <p>
                                    {selectedSub.productCount || 0} listings
                                    {selectedSub.children?.length > 0 && ` • ${selectedSub.children.length} types`}
                                </p>
                            </div>
                        </div>
                        <button
                            className="viewAllBtn"
                            onClick={() => navigateToProducts(selectedSub.mainName, selectedSub.name)}
                        >
                            View All Listings <FaChevronRight />
                        </button>
                    </div>

                    <div className="catChildGrid">
                        {selectedSub.children.map((child, idx) => (
                            <div
                                key={idx}
                                className="catChildCard"
                                onClick={() => handleChildClick(child.name, selectedSub.name, selectedSub.mainName)}
                            >
                                <div className="catChildInfo">
                                    <span className="catChildName">{child.name}</span>
                                    <span className="catChildCount">{child.productCount || 0} listings</span>
                                </div>
                                <FaChevronRight className="catChildArrow" />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    // ── Subcategories view ──────────────────────────────────────────────────
    if (selectedMain) {
        const mainIdx = categoriesData.findIndex(c => c._id === selectedMain._id);
        return (
            <div className="categoriesPage b2bCategoriesPage">
                <SEO title={seoTitle} description={seoDescription} canonicalPath="/b2b/categories" />
                <section className="mainSection">
                    <button className="catBackBtn" onClick={goBack}>
                        <FaArrowLeft /> All B2B Categories
                    </button>

                    <div
                        className="catMainBanner"
                        style={{
                            background: selectedMain.banner
                                ? `url(${selectedMain.banner}) center/cover`
                                : getGradient(mainIdx >= 0 ? mainIdx : 0),
                        }}
                    >
                        <div className="catMainBannerOverlay">
                            <span className="catMainBannerIcon">{selectedMain.icon}</span>
                            <h1>{selectedMain.name}</h1>
                            <p>
                                {selectedMain.productCount || 0} listings •{' '}
                                {selectedMain.subcategories?.length || 0} subcategories
                            </p>
                            <div className="catMainBannerMeta">
                                <span className="metaBadge b2bBadge">🌍 B2B Wholesale</span>
                                <span className="metaBadge">
                                    {selectedMain.type === 'service' ? '🛎️ Services' : '📦 Products'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="catSubGrid">
                        {selectedMain.subcategories?.map((sub, idx) => (
                            <div
                                key={idx}
                                className="catSubCard"
                                onClick={() => handleSubClick(sub, selectedMain.name)}
                            >
                                <div className="catSubLeft">
                                    {sub.icon && <span className="catSubIcon">{sub.icon}</span>}
                                    <div className="catSubInfo">
                                        <span className="catSubName">{sub.name}</span>
                                        <span className="catSubCount">
                                            {sub.productCount || 0} listings
                                            {sub.children?.length > 0 && ` • ${sub.children.length} types`}
                                        </span>
                                    </div>
                                </div>
                                <FaChevronRight className="catSubArrow" />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    // ── Main B2B categories grid ────────────────────────────────────────────
    return (
        <div className="categoriesPage b2bCategoriesPage">
            <SEO title={seoTitle} description={seoDescription} canonicalPath="/b2b/categories" />
            <section className="mainSection">
                <div className="catPageHeader b2bCatPageHeader">
                    <span className="b2bCatPageBadge">B2B Wholesale</span>
                    <h1>B2B Categories</h1>
                    <p>Browse wholesale categories tailored to your business needs</p>
                </div>

                <div className="catGrid">
                    {categoriesData.map((cat, idx) => (
                        <div
                            key={cat._id}
                            className="catCard b2bCatCard"
                            onClick={() => handleMainClick(cat)}
                        >
                            <div
                                className="catCardBanner"
                                style={{
                                    background: cat.banner
                                        ? `url(${cat.banner}) center/cover`
                                        : getGradient(idx),
                                }}
                            >
                                <div className="catCardBannerOverlay">
                                    <span className="catCardIcon">{cat.icon}</span>
                                </div>
                            </div>
                            <div className="catCardBody">
                                <h3 className="catCardTitle">{cat.name}</h3>
                                <div className="catCardMeta">
                                    <span className="catCardCount">{cat.productCount || 0} listings</span>
                                    <span className="catCardSubs">
                                        {cat.subcategories?.length || 0} subcategories
                                    </span>
                                </div>
                                <div className="catCardBadges">
                                    <span className="catCardBadge b2bBadge">🌍 B2B</span>
                                    <span className={`catCardBadge ${cat.type}`}>
                                        {cat.type === 'service' ? '🛎️ Service' : '📦 Product'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default B2BCategories;
